import React, { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import isDev from 'Helpers/isDev';

/**
 * A private component whose sole purpose is to throw the error passed to it.
 * This component is used internally by ThrowInDev and must be a child
 * of a Sentry.ErrorBoundary to work correctly.
 */
const ErrorThrower = ({ err }) => {
  // useEffect ensures the error is thrown exactly once on component mount.
  // This is a render-time error that a parent ErrorBoundary can catch.
  useEffect(() => {
    throw err;
  }, []);

  return null;
};

/**
 * A reusable component that behaves differently based on the environment.
 * In development, it throws the error for immediate debugging. In production,
 * it uses a Sentry.ErrorBoundary to capture the error with the component stack.
 *
 * @param {object} props
 * @param {Error|string} props.err - The error object or a message string.
 */
const ThrowInDev = ({ err, fallback = null }) => {
  // Normalize the input to an Error object if it's a string.
  const errorToThrow = typeof err === 'string' ? new Error(err) : err;

  if (isDev()) {
    // In development, throw the error directly for local debugging.
    throw errorToThrow;
  }

  // In production, use the Sentry.ErrorBoundary to capture the error
  // with the full component stack.
  return (
    <Sentry.ErrorBoundary fallback={fallback}>
      <ErrorThrower err={errorToThrow} />
    </Sentry.ErrorBoundary>
  );
};

export default ThrowInDev;
